import { DEFAULT_COLUMN_WIDTH } from './data-grid.types';
import type { DataGridColumn, DataGridColumnSection } from './data-grid.types';

/**
 * Resolves the effective width for a column using the layered priority cascade:
 * 1. Active drag resize width
 * 2. User-persisted width
 * 3. Column definition width
 * 4. Default column width
 *
 * @param col - The column definition.
 * @param activeResizeWidths - Live widths during an active resize drag.
 * @param persistedWidths - User-persisted widths keyed by field.
 * @param defaultWidth - Fallback width when none of the layers apply.
 * @returns The resolved width in pixels.
 */
export function resolveColumnWidth(
  col: DataGridColumn,
  activeResizeWidths: Record<string, number>,
  persistedWidths: Record<string, number>,
  defaultWidth: number = DEFAULT_COLUMN_WIDTH
): number {
  const activeWidth: number | undefined = activeResizeWidths[col.field];
  if (activeWidth !== undefined) {
    return activeWidth;
  }
  const userWidth: number | undefined = persistedWidths[col.field];
  if (userWidth !== undefined) {
    return userWidth;
  }
  return col.width ?? defaultWidth;
}

/**
 * Computes the total fixed-width (px) of all columns including the action column.
 *
 * @param columns - The visible column definitions for this table context.
 * @param actionColumnWidth - Width of the leading row-action column, or 0.
 * @param resolveWidth - Width resolver for each data column.
 * @param selectionColumnWidth - Width of the leading selection checkbox column, or 0.
 * @returns Sum of selection + action + column widths in pixels.
 */
export function totalColumnWidth(
  columns: Array<DataGridColumn>,
  actionColumnWidth: number,
  resolveWidth: (col: DataGridColumn) => number,
  selectionColumnWidth: number = 0
): number {
  let total: number = selectionColumnWidth + actionColumnWidth;
  for (const col of columns) {
    total += resolveWidth(col);
  }
  return total;
}

/**
 * Builds a CSS `grid-template-columns` string from resolved column widths.
 *
 * @param columns - The visible column definitions.
 * @param actionColumnWidth - Width of the leading row-action column, or 0.
 * @param resolveWidth - Width resolver for each data column.
 * @param selectionColumnWidth - Width of the leading selection checkbox column, or 0.
 * @returns Grid track list ending with a `1fr` filler.
 */
export function gridTemplateColumnsFor(
  columns: Array<DataGridColumn>,
  actionColumnWidth: number,
  resolveWidth: (col: DataGridColumn) => number,
  selectionColumnWidth: number = 0
): string {
  const widths: string = columns.map((col: DataGridColumn): string => `${resolveWidth(col)}px`).join(' ');
  const selectionPrefix: string = selectionColumnWidth > 0 ? `${selectionColumnWidth}px ` : '';
  const actionPrefix: string = actionColumnWidth > 0 ? `${actionColumnWidth}px ` : '';
  return `${selectionPrefix}${actionPrefix}${widths} 1fr`;
}

/**
 * Filters out columns the user has hidden via the Columns popover.
 *
 * @param columns - The full column list.
 * @param hidden - Hidden field names.
 * @returns Visible columns only.
 */
export function filterHiddenColumns(
  columns: Array<DataGridColumn>,
  hidden: Set<string>
): Array<DataGridColumn> {
  if (hidden.size === 0) {
    return columns;
  }
  return columns.filter((col: DataGridColumn): boolean => !hidden.has(col.field));
}

/**
 * Reorders columns according to a persisted order array.
 *
 * Columns in the order array appear first in that order; remaining columns
 * keep their original relative position. Returns `columns` when order is empty.
 *
 * @param columns - The full column list.
 * @param order - The persisted field order.
 * @returns Reordered column list.
 */
export function applyColumnOrder(
  columns: Array<DataGridColumn>,
  order: Array<string>
): Array<DataGridColumn> {
  if (order.length === 0) {
    return columns;
  }
  const columnMap: Map<string, DataGridColumn> = new Map<string, DataGridColumn>();
  for (const col of columns) {
    columnMap.set(col.field, col);
  }
  const ordered: Array<DataGridColumn> = new Array<DataGridColumn>();
  for (const field of order) {
    const col: DataGridColumn | undefined = columnMap.get(field);
    if (col !== undefined) {
      ordered.push(col);
      columnMap.delete(field);
    }
  }
  for (const col of columnMap.values()) {
    ordered.push(col);
  }
  return ordered;
}

/** Inputs for {@link buildColumnSections}. */
export interface BuildColumnSectionsOptions {
  /** Shared column definitions. */
  readonly sharedColumns: Array<DataGridColumn>;
  /** Persisted shared column field order. */
  readonly columnOrder: Array<string>;
  /** Type-specific column definitions keyed by discriminator value. */
  readonly discriminatorColumns: Record<string, Array<DataGridColumn>>;
  /** Persisted type-specific column order keyed by discriminator value. */
  readonly discriminatorColumnOrder: Record<string, Array<string>>;
  /** Discriminator value → display name for section titles. */
  readonly discriminatorLabels: Record<string, string>;
  /** When true, append a section per discriminator type. */
  readonly includeDiscriminatorSections: boolean;
}

/**
 * Builds Columns-popover sections: shared columns first, then optional type groups.
 *
 * @param options - Column definitions, persisted order, and section flags.
 * @returns Sections in grid display order.
 */
export function buildColumnSections(options: BuildColumnSectionsOptions): Array<DataGridColumnSection> {
  const sections: Array<DataGridColumnSection> = new Array<DataGridColumnSection>();
  sections.push({
    sectionId: 'shared',
    icon: 'layers',
    columns: applyColumnOrder(options.sharedColumns, options.columnOrder)
  });

  if (!options.includeDiscriminatorSections) {
    return sections;
  }

  for (const [typeName, columns] of Object.entries(options.discriminatorColumns)) {
    sections.push({
      sectionId: `type:${typeName}`,
      icon: 'category',
      typeDisplayName: options.discriminatorLabels[typeName] ?? typeName,
      columns: applyColumnOrder(columns, options.discriminatorColumnOrder[typeName] ?? new Array<string>())
    });
  }

  return sections;
}

/**
 * Collects field names whose column definitions are hidden by default.
 *
 * @param sharedColumns - Shared column definitions.
 * @param discriminatorColumns - Type-specific column definitions.
 * @returns Field names with `hidden: true`.
 */
export function collectHiddenByDefaultFields(
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>
): Set<string> {
  const hidden: Set<string> = new Set<string>();
  const allColumns: Array<DataGridColumn> = [...sharedColumns, ...Object.values(discriminatorColumns).flat()];
  for (const column of allColumns) {
    if (column.hidden === true) {
      hidden.add(column.field);
    }
  }
  return hidden;
}
